import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import { useEffect, Suspense, type ComponentType } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { lazyWithReload } from "./lib/lazyWithReload";
import { ThemeProvider } from "./contexts/ThemeContext";
// Eager-imported: lazy-loading the cookie banner created a Suspense window
// where prerendered HTML showed a non-interactive banner before React mounted.
import CookieBanner from "./components/CookieBanner";
import VideoReviewPopup from "./components/VideoReviewPopup";
import Redirects from "./components/Redirects";

/**
 * Scroll an in-page anchor into view ourselves.
 *
 * `body { overflow-x: hidden }` (index.css — stops horizontal overflow site-wide)
 * makes the body a scroll container, and Chrome then applies its "scroll to
 * fragment" step to that box instead of the document. The URL hash updates but
 * the page never moves, so every #anchor link silently does nothing. Retries
 * for a few frames because the target may still be mounting.
 */
function scrollToHash(hash: string, behavior: ScrollBehavior = "auto") {
  if (!hash || hash === "#") return;
  let tries = 0;
  const attempt = () => {
    let el: Element | null = null;
    try { el = document.querySelector(hash); } catch { return; } // not a valid selector
    if (el) { el.scrollIntoView({ behavior, block: "start" }); return; }
    if (++tries < 20) requestAnimationFrame(attempt);
  };
  requestAnimationFrame(attempt);
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    // Anchor navigation (#testimonials etc.) — go to the target instead of top.
    if (window.location.hash) { scrollToHash(window.location.hash); return; }
    window.scrollTo(0, 0);
  }, [location]);

  // Same-page anchor clicks only change the hash, so they never hit the effect above.
  useEffect(() => {
    const onHashChange = () => scrollToHash(window.location.hash, "smooth");
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  return null;
}

function MetaPixelPageView() {
  const [location] = useLocation();
  useEffect(() => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location]);
  return null;
}

// Eagerly load Home for best FCP/LCP
import Home from "./pages/Home";

// Lazy load all other pages for reduced initial bundle
const TechnicalSeo = lazyWithReload(() => import("./pages/services/TechnicalSeo"));
const SeoAuditStrategy = lazyWithReload(() => import("./pages/services/SeoAuditStrategy"));
const AiSeo = lazyWithReload(() => import("./pages/services/AiSeo"));
const LocalSeo = lazyWithReload(() => import("./pages/services/LocalSeo"));
const ContentProduction = lazyWithReload(() => import("./pages/services/ContentProduction"));
const ProgrammaticSeo = lazyWithReload(() => import("./pages/services/ProgrammaticSeo"));
const LinkBuilding = lazyWithReload(() => import("./pages/services/LinkBuilding"));
const SeoAgency = lazyWithReload(() => import("./pages/services/SeoAgency"));
const WebDesign = lazyWithReload(() => import("./pages/services/WebDesign"));
const WebDevelopment = lazyWithReload(() => import("./pages/services/WebDevelopment"));
const BlogIndex = lazyWithReload(() => import("./pages/blog/BlogIndex"));
const BlogPost = lazyWithReload(() => import("./pages/blog/BlogPost"));
const BlogAuthor = lazyWithReload(() => import("./pages/blog/BlogAuthor"));
const BlogCategory = lazyWithReload(() => import("./pages/blog/BlogCategory"));
const BlogDiagrams = lazyWithReload(() => import("./pages/blog/BlogDiagrams"));
const Method = lazyWithReload(() => import("./pages/Method"));
const About = lazyWithReload(() => import("./pages/About"));
const Careers = lazyWithReload(() => import("./pages/Careers"));
const Work = lazyWithReload(() => import("./pages/Work"));
const Terms = lazyWithReload(() => import("./pages/Terms"));
const TalkToAStrategist = lazyWithReload(() => import("./pages/TalkToAStrategist"));
const FreeVideoReview = lazyWithReload(() => import("./pages/FreeVideoReview"));
const MyReview = lazyWithReload(() => import("./pages/MyReview"));
const CaseStudyPost = lazyWithReload(() => import("./pages/CaseStudyPost"));
const ThankYou = lazyWithReload(() => import("./pages/ThankYou"));
const BookAMeeting = lazyWithReload(() => import("./pages/BookAMeeting"));
const ThankYouVsl = lazyWithReload(() => import("./pages/ThankYouVsl"));
const Privacy = lazyWithReload(() => import("./pages/Privacy"));
const IndustryPage = lazyWithReload(() => import("./pages/IndustryPage"));
const Industries = lazyWithReload(() => import("./pages/Industries"));
const LocationPage = lazyWithReload(() => import("./pages/LocationPage"));
const LandingPage = lazyWithReload(() => import("./pages/LandingPage"));
const Services = lazyWithReload(() => import("./pages/Services"));
const AdminRouter = lazyWithReload(() => import("./pages/admin/AdminRouter"));
const Sitemap = lazyWithReload(() => import("./pages/Sitemap"));
const ProposalDocPublic = lazyWithReload(() => import("./pages/ProposalDocPublic"));
const ContractPublic = lazyWithReload(() => import("./pages/ContractPublic"));
const ClientHub = lazyWithReload(() => import("./pages/ClientHub"));
const SeoAuditReport = lazyWithReload(() => import("./pages/SeoAuditReport"));
const SignalShare = lazyWithReload(() => import("./pages/SignalShare"));
const VisibilityChecker = lazyWithReload(() => import("./pages/tools/VisibilityChecker"));
const VisibilityCheckerLoading = lazyWithReload(() => import("./pages/tools/VisibilityCheckerLoading"));
const VisibilityCheckerReport = lazyWithReload(() => import("./pages/tools/VisibilityCheckerReport"));
const VisibilityCheckerThankYou = lazyWithReload(() => import("./pages/tools/VisibilityCheckerThankYou"));
const BloggerOutreach = lazyWithReload(() => import("./pages/BloggerOutreach"));
const BloggerOutreachCheckout = lazyWithReload(() => import("./pages/BloggerOutreachCheckout"));
const LinkBuildingPortal = lazyWithReload(() => import("./pages/LinkBuildingPortal"));
const ToolsIndex = lazyWithReload(() => import("./pages/tools/ToolsIndex"));
const DomainRatingChecker = lazyWithReload(() => import("./pages/tools/DomainRatingChecker"));
const DomainRatingCheckerReport = lazyWithReload(() => import("./pages/tools/DomainRatingCheckerReport"));
// Research / Data pages
const ResearchHub = lazyWithReload(() => import("./pages/research/ResearchHub"));
const ResearchDataLanding = lazyWithReload(() => import("./pages/research/ResearchDataLanding"));
const SeoBenchmarksByIndustry = lazyWithReload(() => import("./pages/research/SeoBenchmarksByIndustry"));
const B2bSerpFeatures = lazyWithReload(() => import("./pages/research/B2bSerpFeatures"));
const AiOverviewByFunnelStage = lazyWithReload(() => import("./pages/research/AiOverviewByFunnelStage"));
const B2bCtrByPosition = lazyWithReload(() => import("./pages/research/B2bCtrByPosition"));
const DomainRatingBenchmarks = lazyWithReload(() => import("./pages/research/DomainRatingBenchmarks"));
const KeywordIntentDistribution = lazyWithReload(() => import("./pages/research/KeywordIntentDistribution"));
const ContentLengthBenchmarks = lazyWithReload(() => import("./pages/research/ContentLengthBenchmarks"));
const SearchVolumeTrends = lazyWithReload(() => import("./pages/research/SearchVolumeTrends"));
const CpcVsOrganicValue = lazyWithReload(() => import("./pages/research/CpcVsOrganicValue"));
const QuestionKeywordsAiOverview = lazyWithReload(() => import("./pages/research/QuestionKeywordsAiOverview"));
const BacklinkBenchmarks = lazyWithReload(() => import("./pages/research/BacklinkBenchmarks"));
const ResearchIndex = lazyWithReload(() => import("./pages/research/ResearchIndex"));
const CtrStudy2026 = lazyWithReload(() => import("./pages/research/CtrStudy2026"));
const AiOverviewByIndustry2026 = lazyWithReload(() => import("./pages/research/AiOverviewByIndustry2026"));
// Learn pages
const LearnIndex = lazyWithReload(() => import("./pages/learn/LearnIndex"));
const Glossary = lazyWithReload(() => import("./pages/learn/Glossary"));
const Videos = lazyWithReload(() => import("./pages/Videos"));
const NewsletterArchive = lazyWithReload(() => import("./pages/newsletter/NewsletterArchive"));
const NewsletterIssue = lazyWithReload(() => import("./pages/newsletter/NewsletterIssue"));
const GlossaryTerm = lazyWithReload(() => import("./pages/learn/GlossaryTerm"));
// PPC "Revenue-Driven SEO" landing page (inline lead-capture forms, no funnel).
// PPC traffic only — parallel lead flow, does NOT touch /talk-to-a-strategist.
const RevenueDrivenSeo = lazyWithReload(() => import("./pages/RevenueDrivenSeo"));
// $199 Search Growth Blueprint campaign — noindex, not prerendered, not linked.
const SearchGrowthBlueprint = lazyWithReload(() => import("./pages/SearchGrowthBlueprint"));
const PlaybookThankYou = lazyWithReload(() => import("./pages/PlaybookThankYou"));
const PlaybookPortal = lazyWithReload(() => import("./pages/PlaybookPortal"));
const SearchGrowthAds = lazyWithReload(() => import("./pages/internal/SearchGrowthAds"));
const SearchGrowthOffers = lazyWithReload(() => import("./pages/internal/SearchGrowthOffers"));

// tRPC/react-query providers (+ sonner Toaster) — lazy, mounted ONLY around the
// routes that call the API (blog-post subscribe + admin). Keeps ~45KB of
// trpc/query/superjson/sonner off every marketing page's critical path. See
// [[mobile-bundle-perf]]. Wrapped components are built ONCE at module scope —
// never inline in `component={}`, which would remount the page each render.
const ApiProviders = lazyWithReload(() => import("./ApiProviders"));
const withApi = (Page: ComponentType) => {
  const Wrapped = () => (
    <ApiProviders>
      <Page />
    </ApiProviders>
  );
  return Wrapped;
};
const BlogPostWithApi = withApi(BlogPost);
const AdminRouterWithApi = withApi(AdminRouter);

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <>
    <ScrollToTop />
    <MetaPixelPageView />
    <Suspense fallback={null}>
    <Switch>
      <Route path={"/"} component={Home} />
      {/* Main category pages */}
      <Route path={"/services"} component={Services} />
      <Route path={"/seo-agency"} component={SeoAgency} />
      <Route path={"/web-design"} component={WebDesign} />
      <Route path={"/web-development"} component={WebDevelopment} />
      {/* SEO sub-category pages */}
      <Route path={"/technical-seo"} component={TechnicalSeo} />
      <Route path={"/seo-audit-and-strategy"} component={SeoAuditStrategy} />
      <Route path={"/ai-seo"} component={AiSeo} />
      <Route path={"/local-seo"} component={LocalSeo} />
      <Route path={"/content-production"} component={ContentProduction} />
      <Route path={"/programmatic-seo"} component={ProgrammaticSeo} />
      <Route path={"/link-building"} component={LinkBuilding} />
      {/* Blog routes — order matters: specific before dynamic */}
      <Route path={"/method"} component={Method} />
      <Route path={"/about"} component={About} />
      <Route path={"/careers"} component={Careers} />
      <Route path={"/client-stories"} component={Work} />
      <Route path={"/blog"} component={BlogIndex} />
      <Route path={"/author/:slug"} component={BlogAuthor} />
      <Route path={"/blog/author/:slug"} component={BlogAuthor} />
      <Route path={"/blog/category/:slug"} component={BlogCategory} />
      <Route path={"/blog/diagrams"} component={BlogDiagrams} />
      <Route path={"/blog/:slug"} component={BlogPostWithApi} />
      <Route path={"/client-stories/:slug"} component={CaseStudyPost} />
      <Route path={"/talk-to-a-strategist"} component={TalkToAStrategist} />
      <Route path={"/free-video-review"} component={FreeVideoReview} />
      {/* Token-gated delivery page — noIndex, reachable only by its emailed link. */}
      <Route path={"/my-review/:token"} component={MyReview} />
      <Route path={"/book-a-meeting"} component={BookAMeeting} />
      <Route path={"/book-a-call"} component={ThankYou} />
      <Route path={"/thank-you-booking-a-call"} component={ThankYouVsl} />
      <Route path={"/terms-and-conditions"} component={Terms} />
      <Route path={"/privacy-policy"} component={Privacy} />
      {/* Research / Data pages */}
      <Route path={"/research"} component={ResearchHub} />
      <Route path={"/data"} component={ResearchDataLanding} />
      <Route path={"/data/seo-benchmarks-by-industry"} component={SeoBenchmarksByIndustry} />
      <Route path={"/data/b2b-serp-features-2026"} component={B2bSerpFeatures} />
      <Route path={"/data/ai-overview-by-funnel-stage"} component={AiOverviewByFunnelStage} />
      <Route path={"/data/b2b-ctr-by-position-2026"} component={B2bCtrByPosition} />
      <Route path={"/data/domain-rating-benchmarks-b2b"} component={DomainRatingBenchmarks} />
      <Route path={"/data/b2b-keyword-intent-distribution"} component={KeywordIntentDistribution} />
      <Route path={"/data/content-length-benchmarks-b2b"} component={ContentLengthBenchmarks} />
      <Route path={"/data/b2b-keyword-search-volume-trends"} component={SearchVolumeTrends} />
      <Route path={"/data/cpc-vs-organic-value-b2b"} component={CpcVsOrganicValue} />
      <Route path={"/data/question-keywords-ai-overview-rate"} component={QuestionKeywordsAiOverview} />
      <Route path={"/data/backlink-benchmarks-b2b"} component={BacklinkBenchmarks} />
      <Route path={"/studies"} component={ResearchIndex} />
      <Route path={"/studies/ctr-study-2026"} component={CtrStudy2026} />
      <Route path={"/studies/ai-overview-by-industry-2026"} component={AiOverviewByIndustry2026} />
      {/* PPC Revenue-Driven SEO landing page (paid traffic only, inline forms) */}
      <Route path={"/revenue-driven-seo"} component={RevenueDrivenSeo} />
      <Route path={"/ai-visibility-playbook"} component={SearchGrowthBlueprint} />
      <Route path={"/ai-visibility-playbook/thank-you"} component={PlaybookThankYou} />
      {/* First name for the same page; kept so earlier preview links still open. */}
      <Route path={"/search-growth-blueprint"} component={SearchGrowthBlueprint} />
      {/* Internal campaign previews — unlisted, noindex, disallowed in robots.txt */}
      <Route path={"/internal/search-growth-ads"} component={SearchGrowthAds} />
      <Route path={"/internal/search-growth-offers"} component={SearchGrowthOffers} />
      {/* Learn pages */}
      <Route path={"/learn"} component={LearnIndex} />
      <Route path={"/videos"} component={Videos} />
      <Route path={"/glossary"} component={Glossary} />
      <Route path={"/glossary/:slug"} component={GlossaryTerm} />
      {/* Public newsletter archive (client/src/lib/newsletterArchive.ts) */}
      <Route path={"/newsletter"} component={NewsletterArchive} />
      <Route path={"/newsletter/:slot"} component={NewsletterIssue} />
      {/* Tools pages */}
      <Route path={"/tools"} component={ToolsIndex} />
      <Route path={"/sitemap"} component={Sitemap} />
      {/* AI Visibility Checker tool */}
      <Route path={"/tools/llm-visibility-checker"} component={VisibilityChecker} />
      <Route path={"/tools/llm-visibility-checker/loading"} component={VisibilityCheckerLoading} />
      <Route path={"/tools/llm-visibility-checker/report"} component={VisibilityCheckerReport} />
      <Route path={"/tools/llm-visibility-checker/thank-you"} component={VisibilityCheckerThankYou} />
      <Route path={"/blogger-outreach"} component={BloggerOutreach} />
      <Route path={"/blogger-outreach/checkout"} component={BloggerOutreachCheckout} />
      <Route path={"/portal"} component={LinkBuildingPortal} />
      <Route path={"/portal/:orderId"} component={LinkBuildingPortal} />
      {/* Domain Rating (DR) Checker tool */}
      <Route path={"/tools/domain-rating-checker"} component={DomainRatingChecker} />
      <Route path={"/tools/domain-rating-checker/report"} component={DomainRatingCheckerReport} />
      {/* Brain-dump proposals — short link, token-gated, noindex */}
      <Route path="/p/:token" component={ProposalDocPublic} />
      {/* Client hub — one link gathering a client's review, proposal and the rest */}
      <Route path="/hub/:token" component={ClientHub} />
      <Route path="/playbook/:token" component={PlaybookPortal} />
      {/* SEO issues found on a prospect's site — sent as a link, or framed in a hub */}
      <Route path="/seo-audit/:token" component={SeoAuditReport} />
      {/* An agreement on its own page — where the signing email points */}
      <Route path="/contract/:token" component={ContractPublic} />
      {/* Indexed Signal — client-facing AI visibility share link */}
      <Route path="/v/:token" component={SignalShare} />
      {/* Admin section — password protected */}
      {/* "/admin/*", not "/admin/:rest*": wouter 3 reads ":rest*" as one segment, so /admin/playbook-orders/:session fell through to the 404 */}
      <Route path="/admin/*" component={AdminRouterWithApi} />
      <Route path="/admin" component={AdminRouterWithApi} />
      {/* Industry programmatic SEO pages */}
      <Route path={"/industries"} component={Industries} />
      <Route path={"/industries/:slug"} component={IndustryPage} />
      {/* Location programmatic SEO pages — city route must be before state route */}
      <Route path={"/seo-agency/:state/:city"} component={LocationPage} />
      <Route path={"/seo-agency/:state"} component={LocationPage} />
      {/* Commercial landing pages — root-level /{slug}. MUST stay last (before
          the 404 fallback) so every explicit route above wins; LandingPage
          renders its own not-found view for slugs with no data. */}
      <Route path={"/:slug"} component={LandingPage} />
      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
    </Suspense>
    </>
  );
}

// Routes where global overlays (popup, cookie banner) should be suppressed
const CLEAN_ROUTES = ["/v/", "/admin/", "/p/", "/hub/", "/playbook/", "/contract/", "/revenue-driven-seo", "/search-growth-blueprint", "/ai-visibility-playbook", "/internal/"];
function useIsCleanRoute() {
  const [loc] = useLocation();
  return CLEAN_ROUTES.some(prefix => loc.startsWith(prefix));
}

function GlobalOverlays() {
  const isClean = useIsCleanRoute();
  const [loc] = useLocation();
  if (isClean) return null;
  return (
    <>
      <CookieBanner />
      {/* Scroll-triggered video-review CTA — homepage only for now, where the
          scroll is long enough for it to earn its place. */}
      {loc === "/" && <VideoReviewPopup />}
    </>
  );
}

// Make EVERY internal link client-side, not just the ones wrapped in <Link>.
// Most in-content CTAs, footer links, etc. are plain <a href="/...">, which do a
// full page reload — and a full reload repaints the mobile-prerendered layout and
// snaps it to desktop (the "hero moves around" when you land). This single
// document-level click handler intercepts internal-link clicks anywhere on the
// site and routes them through wouter instead, so navigation is always
// client-side and never snaps — no matter where you click from.
function GlobalLinkInterceptor() {
  const [, navigate] = useLocation();
  useEffect(() => {
    function onClick(e: MouseEvent) {
      // Leave modified / non-left / already-handled clicks (incl. wouter <Link>,
      // which preventDefaults first) to their normal behavior.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as Element | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      // Internal, absolute-path links only. Excludes #hash (browser scroll),
      // external http(s)://, protocol-relative //, mailto:, tel:.
      if (!href || !href.startsWith("/") || href.startsWith("//")) return;
      // Respect new-tab targets, downloads, and explicit external rel.
      const target = anchor.getAttribute("target");
      if (target && target !== "_self") return;
      if (anchor.hasAttribute("download")) return;
      if ((anchor.getAttribute("rel") || "").includes("external")) return;
      e.preventDefault();
      navigate(href);
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Redirects />
          <GlobalOverlays />
          <GlobalLinkInterceptor />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
